import React, { useState, useCallback, useEffect } from 'react';
import {
  useSearchParams,
  useNavigate,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme, alpha } from '@mui/material/styles';
import {
  HealthAndSafetyRounded,
  PersonRounded,
  LocalHospitalRounded,
  HealingRounded,
  MedicationRounded,
  WarningAmberRounded,
  PeopleRounded,
  AutoAwesomeRounded,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from 'hooks/useAuth';
import Logo from 'components/styled/Logo';
import {
  consumeShareCodeAndFetchRecord,
  getTokenValidityLabel,
} from 'services/healthRecordsShareService';
import { setPendingSharedRecord } from 'services/sharedRecordHandoff';

// Match the icon + color each record section uses on the patient side so the two
// screens read as the same record from two angles.
const SECTION_META = {
  demographics: { label: 'Demographics', icon: PersonRounded, color: 'primary' },
  conditions: { label: 'Medical Conditions', icon: LocalHospitalRounded, color: 'info' },
  surgical: { label: 'Surgical History', icon: HealingRounded, color: 'error' },
  medications: { label: 'Medications', icon: MedicationRounded, color: 'success' },
  allergies: { label: 'Allergies', icon: WarningAmberRounded, color: 'warning' },
  other: { label: 'Other History', icon: PeopleRounded, color: 'secondary' },
};

const OTHER_FIELDS = [
  { key: 'smokingStatus', label: 'Smoking' },
  { key: 'alcoholUse', label: 'Alcohol use' },
  { key: 'drugUse', label: 'Drug use' },
  { key: 'occupation', label: 'Occupation' },
  { key: 'familyHistory', label: 'Family history' },
];

const formatDob = (dob) => {
  if (!dob) return null;
  const parsed = parseISO(dob);
  return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : dob;
};

const hasItems = (arr) => Array.isArray(arr) && arr.length > 0;

// A labelled line: primary text with optional muted detail underneath.
const Entry = ({ primary, secondary }) => (
  <ListItem disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
    <ListItemText
      primary={primary}
      secondary={secondary || null}
      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
      secondaryTypographyProps={{ variant: 'caption' }}
    />
  </ListItem>
);

const SectionCard = ({ id, children }) => {
  const theme = useTheme();
  const meta = SECTION_META[id];
  const Icon = meta.icon;
  const palette = theme.palette[meta.color] ?? theme.palette.primary;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 1.5,
            bgcolor: alpha(palette.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 18, color: `${meta.color}.main` }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {meta.label}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
};

// Render one section's body from the redeemed record. Returns null when the
// section was either not shared or has no content worth showing.
const renderSectionBody = (id, record) => {
  switch (id) {
    case 'demographics': {
      const d = record.demographics;
      if (!d) return null;
      const rows = [
        d.dateOfBirth && { label: 'Date of birth', value: formatDob(d.dateOfBirth) },
        d.sex && { label: 'Sex', value: d.sex },
        d.weight != null && { label: 'Weight', value: `${d.weight}` },
      ].filter(Boolean);
      if (rows.length === 0) return null;
      return (
        <List disablePadding>
          {rows.map((r) => (
            <Entry key={r.label} primary={r.label} secondary={r.value} />
          ))}
        </List>
      );
    }
    case 'conditions':
      if (!hasItems(record.conditions)) return null;
      return (
        <List disablePadding>
          {record.conditions.map((c, i) => (
            <Entry key={i} primary={c.condition} secondary={c.notes} />
          ))}
        </List>
      );
    case 'surgical':
      if (!hasItems(record.surgical)) return null;
      return (
        <List disablePadding>
          {record.surgical.map((s, i) => (
            <Entry
              key={i}
              primary={s.procedure}
              secondary={[s.year, s.notes].filter(Boolean).join(' · ')}
            />
          ))}
        </List>
      );
    case 'allergies':
      if (!hasItems(record.allergies)) return null;
      return (
        <List disablePadding>
          {record.allergies.map((a, i) => (
            <Entry
              key={i}
              primary={a.allergen}
              secondary={[a.reaction, a.notes].filter(Boolean).join(' · ')}
            />
          ))}
        </List>
      );
    case 'medications':
      if (!hasItems(record.medications)) return null;
      return (
        <List disablePadding>
          {record.medications.map((m, i) => (
            <Entry
              key={i}
              primary={m.name}
              secondary={[m.dosage, m.frequency].filter(Boolean).join(' · ')}
            />
          ))}
        </List>
      );
    case 'other': {
      const o = record.other;
      if (!o) return null;
      const rows = OTHER_FIELDS.map((f) => o[f.key] && { ...f, value: o[f.key] }).filter(
        Boolean
      );
      if (rows.length === 0) return null;
      return (
        <List disablePadding>
          {rows.map((r) => (
            <Entry key={r.key} primary={r.label} secondary={r.value} />
          ))}
        </List>
      );
    }
    default:
      return null;
  }
};

/**
 * Public provider-facing landing page (route: /share?c=NNNNNNN).
 *
 * Reached when a provider scans the QR a patient generated on "Share with a
 * provider". The provider signs in (or creates an account), redeems the
 * single-use code, and reads the consented record right here. From here they can
 * start a ChartMind encounter seeded with the same record.
 *
 * TODO(security): redemption is auth-gated and audited, but any signed-up account
 * can currently redeem. Before wider rollout, gate to verified provider accounts
 * (e.g. NPI lookup or admin approval). Tracked with the client.
 */
const ShareLandingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userLoading } = useAuth();

  const code = (searchParams.get('c') || '').replace(/\D/g, '');

  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Reaching this page consumes any pending post-login redirect, so a later visit
  // to the dashboard doesn't bounce back here again. (Set in stashShareReturn.)
  useEffect(() => {
    localStorage.removeItem('pendingShareReturn');
  }, []);

  const handleRedeem = useCallback(async () => {
    setRedeeming(true);
    setError(null);
    try {
      const data = await consumeShareCodeAndFetchRecord({
        manualCode: code,
        acceptCareStart: true,
      });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Could not open this shared record. Please try again.');
    } finally {
      setRedeeming(false);
    }
  }, [code]);

  const handleStartEncounter = useCallback(() => {
    setPendingSharedRecord(result);
    navigate('/dashboard/chartmind');
  }, [result, navigate]);

  const handleStartHud2 = useCallback(() => {
    setPendingSharedRecord(result);
    navigate('/clinical-hud-2');
  }, [result, navigate]);

  // Persist the intended role so it survives the email-verification round-trip
  // (which drops the ?role= query before /onboarding). localStorage, not
  // sessionStorage: clicking the verification link in an email opens a fresh tab
  // with empty sessionStorage, so the hint has to live in shared, durable storage.
  // Remember where to come back to so we can return the provider to this exact
  // shared record once they finish signing in (consumed in DashboardPage).
  const stashShareReturn = useCallback(() => {
    if (code.length === 7) {
      localStorage.setItem('pendingShareReturn', `/share?c=${code}`);
    }
  }, [code]);

  const handleCreateAccount = useCallback(() => {
    localStorage.setItem('pendingSignupRole', 'patient');
    stashShareReturn();
    navigate('/auth?role=patient');
  }, [navigate, stashShareReturn]);

  const renderBody = () => {
    if (userLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (code.length !== 7) {
      return (
        <Alert severity="error">
          This share link is missing a valid code. Please ask the patient to show
          their share code again.
        </Alert>
      );
    }

    // Redeemed — show the consented record read-only.
    if (result) {
      const sharedIds = result.allowedSections || [];
      const sections = sharedIds
        .map((id) => ({ id, body: renderSectionBody(id, result.record || {}) }))
        .filter((s) => s.body);

      return (
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            {result.patientName || 'Patient record'}
          </Typography>

          {sections.length > 0 ? (
            sections.map((s) => (
              <SectionCard key={s.id} id={s.id}>
                {s.body}
              </SectionCard>
            ))
          ) : (
            <Alert severity="info">
              This patient shared their record, but the selected sections are empty.
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeRounded />}
            onClick={handleStartEncounter}
          >
            Start a ChartMind encounter
          </Button>
          <Button
            size="large"
            startIcon={<AutoAwesomeRounded />}
            onClick={handleStartHud2}
            data-testid="share-landing-start-hud2"
          >
            Start ClinicalHUD2
          </Button>
          <Typography variant="caption" color="text.secondary">
            This record loads into a new encounter. This code is now used and
            cannot be opened again.
          </Typography>
        </Stack>
      );
    }

    // Signed out — explain what this is and route to auth.
    if (!user) {
      return (
        <Stack spacing={2}>
          <Typography variant="body1">
            A patient wants to share their medical record with you. Please sign in
            to view it. Clinicians and staff join through their institution.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              component={RouterLink}
              to="/auth"
              variant="contained"
              onClick={stashShareReturn}
            >
              Sign in
            </Button>
            <Button variant="outlined" onClick={handleCreateAccount}>
              Create account
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            We'll bring you right back here after you sign in.
          </Typography>
        </Stack>
      );
    }

    // Signed in, not yet redeemed.
    return (
      <Stack spacing={2}>
        <Typography variant="body1">
          A patient wants to share their medical record with you. Opening it uses a
          single-use code that expires {getTokenValidityLabel()} after it was
          created.
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          loading={redeeming}
          onClick={handleRedeem}
          data-testid="share-landing-view-record"
        >
          View shared record
        </LoadingButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2,
      }}
    >
      <Paper
        variant="outlined"
        sx={{ p: { xs: 3, sm: 4 }, my: { xs: 2, sm: 6 }, maxWidth: 600, width: '100%' }}
      >
        <Box sx={{ mb: 2.5 }}>
          <Logo />
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <HealthAndSafetyRounded sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>
            Shared medical record
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          A patient is securely sharing their record with you through FusionMSI.
        </Typography>
        {renderBody()}
      </Paper>
    </Box>
  );
};

export default ShareLandingPage;
